import React, { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import { Button, Dropdown, Image, Anchor } from "hesus-ui-kit";
import PropTypes from "prop-types";
import axios from "axios";
import { handleApiError } from "@utils/errorHandler";
import { getDateFormat } from "@utils/datePickerUtils";
import ClipLoader from "react-spinners/ClipLoader";
import PulseLoader from "react-spinners/PulseLoader";

import {
  Name,
  Title,
  ContactInfo,
  ContactName,
  DocumentInfo,
  ContactPhone,
  ContactEmail,
  DocumentTitle,
  DocCreationDate,
  DocumentContainer,
  DocumentsContainer,
  AddMoreDocumentTitle,
  EmptyDocumentsMessage,
  AddDocumentForm,
  FileInput,
  FileInputLabel,
  LoadingContainer,
  DocumentRow,
  FileInputContainer,
  FileName,
  DocumentName,
} from "./Documents.styled";
import "../../SolutionView.scss";

import docIcon from "@icons/ic-document.svg";
import editIcon from "@icons/ic-small-edit.svg";
import trashIcon from "@icons/ic-trash.svg";
import userIcon from "@icons/ic-solid-user.svg";
import addIcon from "@icons/ic-add-orange.svg";
import DocumentPreview from "../../../../Document/DocumentPreview/DocumentPreview";

const Documents = (props) => {
  const { id, companyId, t } = props;

  const [documents, setDocuments] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [isDocumentEdit, setDocumentEdit] = useState(false);
  const [canAddDocument, setAddDocument] = useState(false);
  const [isDocumentSaving, setDocumentSaveStatus] = useState(false);
  const [isDocumentLoading, setDocumentsLoadStatus] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [formData, setFormData] = useState({
    contact: { value: "", error: "", required: true },
    document: [],
    selectedDocumentId: "",
    selectedDocumentName: "",
  });

  const editDocument = (documentId) => {
    const selectedDocument = documents.find((doc) => doc.id === documentId);

    setFormData({
      ...formData,
      contact: {
        value: selectedDocument.contact.id,
        error: "",
        required: true,
      },
      selectedDocumentId: documentId,
      selectedDocumentName: selectedDocument.fileName,
    });
    setDocumentEdit(true);
    setAddDocument(true);
  };

  const deleteDocument = (documentId) => {
    axios
      .delete(`/documents/${documentId}`)
      .then(() => {
        const filteredDocs = documents.filter(
          (document) => document.id !== documentId
        );
        setDocuments(filteredDocs);
      })
      .catch((error) => handleApiError(error));
  };

  const addMoreDocuments = () => {
    setDocumentEdit(false);
    setAddDocument(true);
  };

  const openPreview = (document) => setSelectedDocument(document);
  const closePreview = () => setSelectedDocument(null);

  const onContactSelect = (selectedContact) => {
    setFormData({
      ...formData,
      contact: { value: selectedContact.value, error: "", required: true },
    });
  };

  const closeDocumentForm = () => {
    setAddDocument(false);
    setFormData({
      ...formData,
      contact: {
        value: "",
        error: "",
        required: true,
      },
      document: [],
      selectedDocumentId: "",
      selectedDocumentName: "",
    });
  };

  const onFileSelect = (e) => {
    setFormData({
      ...formData,
      document: e.target.files,
      selectedDocumentName: e.target.files[0].name,
    });
  };

  const isValidateDisabled = () => {
    if (isDocumentEdit) {
      return !formData.contact.value || !formData.selectedDocumentName;
    }

    return !formData.contact.value || formData.document.length === 0;
  };

  const saveDocument = (e) => {
    e.preventDefault();
    const addDocumentForm = document.getElementById("add-document");
    const documentFormData = new FormData(addDocumentForm);
    documentFormData.append("contact[id]", formData.contact.value);
    documentFormData.append("solutionId", id);
    documentFormData.append("type[id]", 11);
    [...formData.document].forEach((file) => {
      documentFormData.append("file", file);
    });
    setDocumentSaveStatus(true);
    if (isDocumentEdit && formData.selectedDocumentId) {
      axios
        .post(
          `/documents/${formData.selectedDocumentId}/update`,
          documentFormData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        )
        .then((response) => {
          const filteredDocs = documents.filter(
            (document) => document.id !== formData.selectedDocumentId
          );
          setAddDocument(false);
          setDocumentSaveStatus(false);
          setDocuments([...filteredDocs, response.data[0]]);
        })
        .catch((error) => handleApiError(error));
    } else {
      axios
        .post("/documents", documentFormData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
        .then((response) => {
          setDocumentSaveStatus(false);
          setDocuments([...documents, response.data[0]]);
          setAddDocument(false);
        })
        .catch((error) => handleApiError(error));
    }
  };

  const renderAddDocumentView = () => {
    return (
      <AddDocumentForm id="add-document">
        <Dropdown
          label={t("CONTACT")}
          name="contact"
          value={parseInt(formData.contact.value, 10)}
          defaultOption={t("CONTACT")}
          inputStyles={{ border: "1px solid #474747" }}
          options={contacts.map((contact) => {
            return {
              value: parseInt(contact.id, 10),
              label: `${contact.firstName ?? ""} ${contact.lastName ?? ""}`,
            };
          })}
          required
          error={formData.contact.error}
          onChange={onContactSelect}
        />
        <FileInputContainer>
          <FileName>
            {formData.document.length > 0
              ? formData.document[0].name
              : isDocumentEdit
              ? formData.selectedDocumentName
              : ""}
          </FileName>
          <FileInput
            type="file"
            id="document"
            onChange={onFileSelect}
            accept=".pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          />
          <FileInputLabel htmlFor="document">Select File</FileInputLabel>
        </FileInputContainer>
        <div>
          <Button
            styles={{
              backgroundColor: "#32A728",
              border: "2px solid #32A728",
              borderRadius: "4px",
              color: "white",
              height: "auto",
              padding: "11px 1rem",
              "&:hover": {
                backgroundColor: "#32A728",
              },
            }}
            disabled={isValidateDisabled()}
            onClick={saveDocument}
          >
            <>
              {isDocumentSaving && <ClipLoader size={15} color="#fff" />}
              {!isDocumentSaving && t("VALIDATE")}
            </>
          </Button>
          <Button
            styles={{
              backgroundColor: "#FF5200",
              border: "2px solid #FF5200",
              borderRadius: "4px",
              color: "white",
              height: "auto",
              marginLeft: "0.75rem",
              padding: "11px 1rem",
              "&:hover": {
                backgroundColor: "#FF5200",
              },
            }}
            onClick={closeDocumentForm}
          >
            <>{t("CANCEL")}</>
          </Button>
        </div>
      </AddDocumentForm>
    );
  };

  useEffect(() => {
    setDocumentsLoadStatus(true);
    axios
      .get(`/documents?solutionId=${id}`)
      .then((response) => {
        setDocumentsLoadStatus(false);
        setDocuments(response.data.documents);
      })
      .catch((error) => handleApiError(error));
  }, [id]);

  useEffect(() => {
    axios
      .get(`/companies/${companyId}/contacts`)
      .then((response) => {
        setContacts(response.data.contacts);
      })
      .catch((error) => handleApiError(error));
  }, [companyId]);

  return (
    <>
      <DocumentsContainer>
        <DocumentTitle>
          <Image
            src={docIcon}
            alt="Supplier Quotes Documents"
            styles={{ width: "auto" }}
          />
          <Title>{t("PARTNER_QUOTE_DOCUMENT")} : </Title>
        </DocumentTitle>

        <DocumentContainer>
          {isDocumentLoading && (
            <LoadingContainer>
              <PulseLoader color="#004c7f" />
            </LoadingContainer>
          )}
          {!isDocumentLoading &&
            documents.length > 0 &&
            documents.map((document) => {
              return (
                <DocumentRow key={document.id}>
                  <DocumentInfo>
                    <DocumentName
                      onClick={() => openPreview(document)}
                      className="solutions-view__document-link"
                    >
                      {document.fileName}
                    </DocumentName>
                    <DocCreationDate>
                      {getDateFormat(document.createdAt, "YYYY-MM-DD")}
                    </DocCreationDate>
                    <Button
                      styles={{
                        backgroundColor: "transparent",
                        border: "none",
                        height: "auto",
                        padding: 0,
                        "&:hover": {
                          backgroundColor: "transparent",
                        },
                      }}
                      onClick={() => deleteDocument(document.id)}
                    >
                      <Image
                        src={trashIcon}
                        alt="Document Delete Action"
                        styles={{ width: "auto" }}
                      />
                    </Button>
                  </DocumentInfo>
                  <ContactInfo>
                    <ContactName>
                      <Image
                        src={userIcon}
                        alt="Contact Icon"
                        styles={{ width: "auto" }}
                      />
                      <Name>{`${document.contact.firstName ?? ""} ${
                        document.contact.lastName ?? ""
                      }`}</Name>
                    </ContactName>
                    <ContactPhone>{document.contact.phone ?? ""}</ContactPhone>
                    <ContactEmail>{document.contact.email ?? ""}</ContactEmail>
                    <Button
                      styles={{
                        backgroundColor: "transparent",
                        border: "none",
                        height: "auto",
                        padding: 0,
                        "&:hover": {
                          backgroundColor: "transparent",
                        },
                      }}
                      onClick={() => editDocument(document.id)}
                    >
                      <Image
                        src={editIcon}
                        alt="edit document"
                        styles={{ width: "auto" }}
                      />
                    </Button>
                  </ContactInfo>
                </DocumentRow>
              );
            })}
          {!isDocumentLoading && documents.length === 0 && (
            <EmptyDocumentsMessage>No documents found</EmptyDocumentsMessage>
          )}
        </DocumentContainer>
        <Button
          styles={{
            backgroundColor: "transparent",
            border: "none",
            height: "auto",
            padding: 0,
            "&:hover": {
              backgroundColor: "transparent",
            },
            textDecoration: "underline",
            textDecorationColor: "#FF5200",
          }}
          onClick={addMoreDocuments}
          disabled={canAddDocument}
        >
          <>
            <Image
              src={addIcon}
              alt="Add Document"
              styles={{ width: "auto" }}
            />
            <AddMoreDocumentTitle>
              {t("SOLUTION_VIEW_ADD_MORE_DOCUMENTS")}
            </AddMoreDocumentTitle>
          </>
        </Button>
        {canAddDocument && renderAddDocumentView()}
      </DocumentsContainer>
      {selectedDocument && (
        <DocumentPreview document={selectedDocument} onClose={closePreview} />
      )}
    </>
  );
};

Documents.propTypes = {
  id: PropTypes.number.isRequired,
  companyId: PropTypes.number.isRequired,
  t: PropTypes.func.isRequired,
};

export default withTranslation()(Documents);
